import { useNavigate, useSearchParams } from "react-router-dom";

import {
  useActivateUserMutation,
  useVerifyTokenQuery
} from "@/api/auth/useAuthAPI";
import LoginPageLayout from "@/components/layout/login";
import { PASSWORD_FORM_TOAST_CONFIG } from "@/components/passwordForm/data";
import ExpiredLink from "@/components/passwordForm/ExpiredLink";
import LoadingVerifyToken from "@/components/passwordForm/LoadingVerifyToken";
import PasswordForm from "@/components/passwordForm/PasswordForm";
import useToast from "@/hooks/ui/useToast";

export const ACTIVATE_USER_TOAST_COPY = {
  success: {
    title: "Account Activated",
    message:
      "You've set your password, and your account is ready. Sign in to start using the platform."
  },
  error: {
    title: "Something Went Wrong",
    message: "Sorry, we couldn't process your request. Please try again."
  }
};

const ActivateUserPage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { isError: verifyTokenError, isPending: verifyTokenPending } =
    useVerifyTokenQuery(searchParams.get("token") ?? "");

  const { mutate: activateUser, isPending } = useActivateUserMutation({
    onSuccess: () => {
      navigate("/login", { replace: true });
      showToast(
        PASSWORD_FORM_TOAST_CONFIG(
          ACTIVATE_USER_TOAST_COPY.success.title,
          ACTIVATE_USER_TOAST_COPY.success.message
        ).successToast
      );
    },
    onError: () => {
      showToast(
        PASSWORD_FORM_TOAST_CONFIG(
          ACTIVATE_USER_TOAST_COPY.error.title,
          ACTIVATE_USER_TOAST_COPY.error.message
        ).errorToast
      );
    }
  });

  if (verifyTokenPending) return <LoadingVerifyToken />;
  if (verifyTokenError)
    return (
      <ExpiredLink topDescriptionText="Your user activation link has expired." />
    );

  return (
    <LoginPageLayout>
      <div className="flex flex-col flex-1 mt-[278px] lg:mt-[278px] xl:mt-[209px] 2xl:mt-[394px] mx-auto w-full">
        <PasswordForm
          mode="createPassword"
          isSubmitting={isPending}
          onSubmit={(password) => {
            activateUser({
              token: searchParams.get("token") as string,
              password
            });
          }}
        />
      </div>
    </LoginPageLayout>
  );
};

export default ActivateUserPage;
